import React, { useState } from "react";
import { Link } from "react-router-dom";
import { all_routes } from "../../router/all_routes";

interface AttendanceRecord {
  id: string;
  date: string;
  class: string;
  subject: string;
  totalStudents: number;
  present: number;
  absent: number;
  late: number;
  percentage: number;
}

const TeacherAttendanceHistory = () => {
  const routes = all_routes;
  const [selectedClass, setSelectedClass] = useState("all");
  const [selectedMonth, setSelectedMonth] = useState("2024-08");
  const [viewType, setViewType] = useState<"daily" | "weekly">("weekly");

  const classes = [
    "all",
    "10A Mathematics",
    "11B Physics",
    "9C Mathematics",
    "12A Advanced Math",
  ];

  const attendanceRecords: AttendanceRecord[] = [
    {
      id: "1",
      date: "2024-08-05",
      class: "10A Mathematics",
      subject: "Mathematics",
      totalStudents: 32,
      present: 30,
      absent: 2,
      late: 0,
      percentage: 93.8,
    },
    {
      id: "2",
      date: "2024-08-05",
      class: "11B Physics",
      subject: "Physics",
      totalStudents: 28,
      present: 26,
      absent: 1,
      late: 1,
      percentage: 92.9,
    },
    {
      id: "3",
      date: "2024-08-04",
      class: "10A Mathematics",
      subject: "Mathematics",
      totalStudents: 32,
      present: 28,
      absent: 3,
      late: 1,
      percentage: 87.5,
    },
    {
      id: "4",
      date: "2024-08-03",
      class: "9C Mathematics",
      subject: "Mathematics",
      totalStudents: 30,
      present: 29,
      absent: 1,
      late: 0,
      percentage: 96.7,
    },
    {
      id: "5",
      date: "2024-08-02",
      class: "12A Advanced Math",
      subject: "Advanced Mathematics",
      totalStudents: 25,
      present: 24,
      absent: 1,
      late: 0,
      percentage: 96.0,
    },
  ];

  const filteredRecords = attendanceRecords.filter((record) => {
    const matchesClass =
      selectedClass === "all" || record.class === selectedClass;
    const recordMonth = record.date.substring(0, 7);
    const matchesMonth = recordMonth === selectedMonth;
    return matchesClass && matchesMonth;
  });

  const getWeekOfYear = (date: string): string => {
    const d = new Date(date);
    const startOfYear = new Date(d.getFullYear(), 0, 1);
    const days = Math.floor(
      (d.getTime() - startOfYear.getTime()) / (24 * 60 * 60 * 1000)
    );
    const weekNumber = Math.ceil((days + startOfYear.getDay() + 1) / 7);
    return `${d.getFullYear()}-W${weekNumber.toString().padStart(2, "0")}`;
  };

  const groupedRecords =
    viewType === "weekly"
      ? filteredRecords.reduce((groups, record) => {
          const week = getWeekOfYear(record.date);
          if (!groups[week]) groups[week] = [];
          groups[week].push(record);
          return groups;
        }, {} as Record<string, AttendanceRecord[]>)
      : filteredRecords.reduce((groups, record) => {
          if (!groups[record.date]) groups[record.date] = [];
          groups[record.date].push(record);
          return groups;
        }, {} as Record<string, AttendanceRecord[]>);

  const getPercentageColor = (percentage: number): string => {
    if (percentage >= 95) return "success";
    if (percentage >= 85) return "warning";
    return "danger";
  };

  const calculateStats = () => {
    const totalRecords = filteredRecords.length;
    const totalStudents = filteredRecords.reduce(
      (sum, r) => sum + r.totalStudents,
      0
    );
    const totalPresent = filteredRecords.reduce((sum, r) => sum + r.present, 0);
    const avgPercentage =
      filteredRecords.length > 0
        ? Math.round(
            filteredRecords.reduce((sum, r) => sum + r.percentage, 0) /
              filteredRecords.length
          )
        : 0;

    return { totalRecords, totalStudents, totalPresent, avgPercentage };
  };

  const getGroupTitle = (key: string): string => {
    if (viewType === "weekly") {
      const [year, week] = key.split("-W");
      return `Week ${week}, ${year}`;
    } else {
      return new Date(key).toLocaleDateString("en-US", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    }
  };

  const stats = calculateStats();

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          {/* Header */}
          <div className="row mb-4">
            <div className="col">
              <h3 className="page-title mb-2">Attendance History</h3>
              <p className="text-muted mb-0">Past attendance records</p>
            </div>
          </div>

          {/* Filters */}
          <div className="card mb-4">
            <div className="card-body">
              <div className="row g-3 align-items-center">
                <div className="col-md-4">
                  <label className="form-label">Filter by Class</label>
                  <select
                    className="form-select"
                    value={selectedClass}
                    onChange={(e) => setSelectedClass(e.target.value)}
                  >
                    {classes.map((cls) => (
                      <option key={cls} value={cls}>
                        {cls === "all" ? "All Classes" : cls}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-4">
                  <label className="form-label">Filter by Month</label>
                  <input
                    type="month"
                    className="form-control"
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(e.target.value)}
                  />
                </div>
                <div className="col-md-4">
                  <label className="form-label">View Type</label>
                  <div className="btn-group w-100">
                    <button
                      className={`btn ${
                        viewType === "daily"
                          ? "btn-primary"
                          : "btn-outline-primary"
                      }`}
                      onClick={() => setViewType("daily")}
                    >
                      Daily
                    </button>
                    <button
                      className={`btn ${
                        viewType === "weekly"
                          ? "btn-primary"
                          : "btn-outline-primary"
                      }`}
                      onClick={() => setViewType("weekly")}
                    >
                      Weekly
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Summary Stats */}
          <div className="row mb-4">
            <div className="col-md-3 col-sm-6">
              <div className="card bg-primary-light">
                <div className="card-body text-center">
                  <h4 className="text-primary">{stats.totalRecords}</h4>
                  <p className="mb-0">Total Sessions</p>
                </div>
              </div>
            </div>
            <div className="col-md-3 col-sm-6">
              <div className="card bg-info-light">
                <div className="card-body text-center">
                  <h4 className="text-info">{stats.totalStudents}</h4>
                  <p className="mb-0">Total Students</p>
                </div>
              </div>
            </div>
            <div className="col-md-3 col-sm-6">
              <div className="card bg-success-light">
                <div className="card-body text-center">
                  <h4 className="text-success">{stats.totalPresent}</h4>
                  <p className="mb-0">Total Present</p>
                </div>
              </div>
            </div>
            <div className="col-md-3 col-sm-6">
              <div className="card bg-warning-light">
                <div className="card-body text-center">
                  <h4 className="text-warning">{stats.avgPercentage}%</h4>
                  <p className="mb-0">Avg Attendance</p>
                </div>
              </div>
            </div>
          </div>

          {/* Attendance Records */}
          <div className="card">
            <div className="card-header">
              <h5 className="card-title mb-0">
                Attendance Records - {viewType === "daily" ? "Daily" : "Weekly"}{" "}
                View
              </h5>
            </div>
            <div className="card-body">
              {Object.keys(groupedRecords).length > 0 ? (
                Object.entries(groupedRecords).map(([groupKey, records]) => (
                  <div key={groupKey} className="mb-4">
                    <h6 className="text-primary mb-3 border-bottom pb-2">
                      {getGroupTitle(groupKey)}
                    </h6>
                    <div className="row g-3">
                      {records.map((record) => (
                        <div key={record.id} className="col-lg-6">
                          <div className="card border">
                            <div className="card-body">
                              <div className="d-flex align-items-start justify-content-between mb-3">
                                <div>
                                  <h6 className="mb-1">{record.class}</h6>
                                  <p className="text-muted mb-0 small">
                                    {viewType === "daily"
                                      ? record.subject
                                      : new Date(
                                          record.date
                                        ).toLocaleDateString("en-US", {
                                          weekday: "short",
                                          month: "short",
                                          day: "numeric",
                                        })}
                                  </p>
                                </div>
                                <span
                                  className={`badge bg-${getPercentageColor(
                                    record.percentage
                                  )}`}
                                >
                                  {record.percentage.toFixed(1)}%
                                </span>
                              </div>

                              <div className="row text-center mb-3">
                                <div className="col-3">
                                  <div className="text-primary">
                                    <strong>{record.totalStudents}</strong>
                                    <p className="mb-0 small text-muted">
                                      Total
                                    </p>
                                  </div>
                                </div>
                                <div className="col-3">
                                  <div className="text-success">
                                    <strong>{record.present}</strong>
                                    <p className="mb-0 small text-muted">
                                      Present
                                    </p>
                                  </div>
                                </div>
                                <div className="col-3">
                                  <div className="text-danger">
                                    <strong>{record.absent}</strong>
                                    <p className="mb-0 small text-muted">
                                      Absent
                                    </p>
                                  </div>
                                </div>
                                <div className="col-3">
                                  <div className="text-warning">
                                    <strong>{record.late}</strong>
                                    <p className="mb-0 small text-muted">
                                      Late
                                    </p>
                                  </div>
                                </div>
                              </div>

                              <div
                                className="progress mb-2"
                                style={{ height: "8px" }}
                              >
                                <div
                                  className={`progress-bar bg-${getPercentageColor(
                                    record.percentage
                                  )}`}
                                  style={{ width: `${record.percentage}%` }}
                                ></div>
                              </div>

                              <div className="d-flex gap-2">
                                <Link
                                  to={routes.attendanceTracker}
                                  className="btn btn-outline-primary btn-sm flex-fill"
                                >
                                  <i className="ti ti-eye me-1"></i>View Details
                                </Link>
                                <Link
                                  to={routes.attendanceTracker}
                                  className="btn btn-outline-secondary btn-sm flex-fill"
                                >
                                  <i className="ti ti-edit me-1"></i>Modify
                                </Link>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-4">
                  <div className="avatar avatar-xl bg-secondary-transparent mb-3">
                    <i className="ti ti-calendar-x fs-1 text-secondary"></i>
                  </div>
                  <h5>No attendance records found</h5>
                  <p className="text-muted">
                    No attendance records match your selected filters.
                  </p>
                  <Link
                    to={routes.attendanceTracker}
                    className="btn btn-primary"
                  >
                    <i className="ti ti-user-check me-2"></i>Take Attendance
                  </Link>
                </div>
              )}
            </div>
          </div>

          {/* Quick Actions */}
          <div className="card mt-4">
            <div className="card-header">
              <h6 className="card-title mb-0">Quick Actions</h6>
            </div>
            <div className="card-body">
              <div className="row g-2">
                <div className="col-md-3 col-sm-6">
                  <Link
                    to={routes.attendanceTracker}
                    className="btn btn-success w-100"
                  >
                    <i className="ti ti-user-check me-2"></i>Take Attendance
                  </Link>
                </div>
                <div className="col-md-3 col-sm-6">
                  <Link
                    to={routes.teachersRoutine}
                    className="btn btn-info w-100"
                  >
                    <i className="ti ti-calendar me-2"></i>View Schedule
                  </Link>
                </div>
                <div className="col-md-3 col-sm-6">
                  <Link
                    to={routes.attendanceReport}
                    className="btn btn-primary w-100"
                  >
                    <i className="ti ti-file-analytics me-2"></i>Generate Report
                  </Link>
                </div>
                <div className="col-md-3 col-sm-6">
                  <Link
                    to={routes.teacherDashboard}
                    className="btn btn-outline-secondary w-100"
                  >
                    <i className="ti ti-home me-2"></i>Dashboard
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default TeacherAttendanceHistory;
